import { router } from "@inertiajs/react"
import { Plus, X } from "lucide-react"
import { useState } from "react"

import { Avatar, Field, Section } from "@/components/steel"
import { Button } from "@/components/ui/button"
import { DataTable, Td, Th, Thead, Tr } from "@/components/steel/table"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { formatCurrency } from "@/lib/utils"
import type { AssignableEmployee, RosterEmployee } from "@/pages/Projects/tabs/types"

function AssignDialog({
  onOpenChange,
  open,
  projectId,
  unassignedEmployees,
}: {
  onOpenChange: (open: boolean) => void
  open: boolean
  projectId: string
  unassignedEmployees: AssignableEmployee[]
}) {
  const [selected, setSelected] = useState<AssignableEmployee | null>(null)
  const [payRate, setPayRate] = useState("")
  const [billRate, setBillRate] = useState("")
  const [role, setRole] = useState("")

  const reset = () => {
    setSelected(null)
    setPayRate("")
    setBillRate("")
    setRole("")
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset()
        onOpenChange(next)
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Assign to this project</DialogTitle>
          <DialogDescription>
            {selected ? "Set the project pay and bill rates." : "Pick who joins the crew."}
          </DialogDescription>
        </DialogHeader>

        {unassignedEmployees.length === 0 ? (
          <p className="py-5 text-center text-label text-muted-foreground">
            Everyone is already on this project.
          </p>
        ) : selected ? (
          <>
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-2.5">
                <Avatar initials={selected.avatar} />
                <span>
                  <span className="block text-body font-medium">{selected.name}</span>
                  <span className="block text-meta text-muted-foreground">{selected.department}</span>
                </span>
              </div>
              <Field label="Pay rate ($/hr)">
                <Input type="number" step="0.01" value={payRate} onChange={(e) => setPayRate(e.target.value)} />
              </Field>
              <Field label="Bill rate ($/hr) — charged to the client">
                <Input type="number" step="0.01" value={billRate} onChange={(e) => setBillRate(e.target.value)} />
              </Field>
              <Field label="Role">
                <Input value={role} onChange={(e) => setRole(e.target.value)} />
              </Field>
            </div>
            <DialogFooter>
              <Button variant="outline" size="sm" onClick={reset}>
                Back
              </Button>
              <Button
                size="sm"
                disabled={!payRate || !role}
                onClick={() => {
                  router.post(
                    `/projects/${projectId}/assignments`,
                    { employeeId: selected.id, payRate, billRate, role },
                    { preserveScroll: true },
                  )
                  reset()
                  onOpenChange(false)
                }}
              >
                Assign
              </Button>
            </DialogFooter>
          </>
        ) : (
          <div className="flex max-h-72 flex-col gap-0.5 overflow-y-auto">
            {unassignedEmployees.map((employee) => (
              <button
                key={employee.id}
                type="button"
                onClick={() => {
                  setSelected(employee)
                  setPayRate(String(employee.hourlyRate))
                  setRole(employee.role)
                }}
                className="flex items-center gap-2.5 rounded-md px-2 py-2 text-left hover:bg-secondary"
              >
                <Avatar initials={employee.avatar} />
                <span className="flex-1">
                  <span className="block text-body font-medium">{employee.name}</span>
                  <span className="block text-meta text-muted-foreground">
                    {employee.role} · {employee.department}
                  </span>
                </span>
                <span className="num text-meta text-muted-foreground">${employee.hourlyRate}/hr</span>
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

export function CrewTab({
  projectId,
  team,
  unassignedEmployees,
}: {
  projectId: string
  team: RosterEmployee[]
  unassignedEmployees: AssignableEmployee[]
}) {
  const [assignOpen, setAssignOpen] = useState(false)
  const [editing, setEditing] = useState<RosterEmployee | null>(null)
  const [payRate, setPayRate] = useState("")
  const [billRate, setBillRate] = useState("")

  const laborCost = team.reduce((sum, e) => sum + e.hourlyRate * e.hours, 0)
  const billable = team.reduce((sum, e) => sum + e.billRate * e.hours, 0)

  return (
    <Section className="pb-7">
      <div className="mb-3.5 flex items-center justify-between gap-4">
        <div>
          <h2 className="text-title font-semibold">Crew</h2>
          <p className="mt-0.5 text-meta text-muted-foreground">
            {team.length} assigned · {formatCurrency(laborCost)} labor cost · {formatCurrency(billable)} billable
          </p>
        </div>
        <Button size="sm" onClick={() => setAssignOpen(true)}>
          <Plus />
          Assign
        </Button>
      </div>

      {team.length === 0 ? (
        <p className="border-t border-border py-8 text-center text-label text-muted-foreground">
          Nobody is assigned to this project yet.
        </p>
      ) : (
        <DataTable>
          <Thead>
            <Th>Employee</Th>
            <Th>Role</Th>
            <Th align="right">Pay</Th>
            <Th align="right">Bill</Th>
            <Th align="right">Hours</Th>
            <Th align="right">Cost</Th>
            <Th align="right">Margin</Th>
            <Th />
          </Thead>
          <tbody>
            {team.map((employee) => (
              <Tr key={employee.id} onClick={() => router.visit(`/team/employees/${employee.id}`)}>
                <Td>
                  <span className="flex items-center gap-2.5">
                    <Avatar initials={employee.avatar} />
                    <span>
                      <span className="block text-body font-medium">{employee.name}</span>
                      <span className="block text-meta text-muted-foreground">{employee.department}</span>
                    </span>
                  </span>
                </Td>
                <Td muted>{employee.role}</Td>
                <Td align="right" num>
                  {formatCurrency(employee.hourlyRate)}
                </Td>
                <Td align="right" num>
                  {employee.billRate ? formatCurrency(employee.billRate) : "—"}
                </Td>
                <Td align="right" num>
                  {employee.hours.toLocaleString()}
                </Td>
                <Td align="right" num>
                  {formatCurrency(employee.hourlyRate * employee.hours)}
                </Td>
                <Td align="right" num accent>
                  {formatCurrency((employee.billRate - employee.hourlyRate) * employee.hours)}
                </Td>
                <Td align="right" className="whitespace-nowrap">
                  <Button
                    variant="ghost"
                    size="xs"
                    onClick={(e) => {
                      e.stopPropagation()
                      setPayRate(String(employee.hourlyRate))
                      setBillRate(String(employee.billRate))
                      setEditing(employee)
                    }}
                  >
                    Rates
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={(e) => {
                      e.stopPropagation()
                      router.delete(`/projects/${projectId}/assignments/${employee.id}`, {
                        preserveScroll: true,
                      })
                    }}
                  >
                    <X />
                  </Button>
                </Td>
              </Tr>
            ))}
          </tbody>
        </DataTable>
      )}

      <AssignDialog
        open={assignOpen}
        onOpenChange={setAssignOpen}
        projectId={projectId}
        unassignedEmployees={unassignedEmployees}
      />

      <Dialog open={editing !== null} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Project rates</DialogTitle>
            <DialogDescription>{editing?.name}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <Field label="Pay rate ($/hr) — what you pay">
              <Input type="number" step="0.01" value={payRate} onChange={(e) => setPayRate(e.target.value)} />
            </Field>
            <Field label="Bill rate ($/hr) — what you charge">
              <Input type="number" step="0.01" value={billRate} onChange={(e) => setBillRate(e.target.value)} />
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={!payRate}
              onClick={() => {
                if (!editing) return
                router.patch(
                  `/projects/${projectId}/assignments/${editing.id}`,
                  { billRate, payRate },
                  { preserveScroll: true, onSuccess: () => setEditing(null) },
                )
              }}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Section>
  )
}
